import type {
  MarketingRouterSignificanceTimeseriesDay,
  MarketingRouterSignificanceTimeseriesPoint,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import { type MarketingRouterMetricKey, formatMetricValue } from './marketingRouterSignificanceDisplay';

/**
 * Pure shaping for the results page graphs. Recharts wants one row per x
 * value with a key per line, so the day-by-day series is pivoted here, once,
 * and the components only draw.
 */

export type TrendMode = 'improvement' | 'value' | 'confidence';

export const TREND_MODES: Array<{ value: TrendMode; label: string }> = [
  { value: 'improvement', label: 'Improvement' },
  { value: 'value', label: 'Metric value' },
  { value: 'confidence', label: 'Confidence' },
];

export type TrendSeries = { key: string; label: string; isControl: boolean };
export type TrendRow = { date: string } & Record<string, number | string | null>;

export type TrendChart = { rows: TrendRow[]; series: TrendSeries[] };

const CONTROL_KEY = '__control';

function pointValue(point: MarketingRouterSignificanceTimeseriesPoint, mode: TrendMode): number | null {
  switch (mode) {
    case 'improvement':
      return point.relativeLift === null ? null : point.relativeLift * 100;
    case 'value':
      return point.treatValue;
    case 'confidence':
      return Number.isFinite(point.pAdjusted) ? (1 - point.pAdjusted) * 100 : null;
  }
}

/**
 * One metric's curve over the run. Improvement and confidence are one line per
 * treatment variant; metric value adds the control as its own line, since the
 * comparison is what the reader is looking for.
 */
export function buildTrendChart(
  days: MarketingRouterSignificanceTimeseriesDay[],
  metric: MarketingRouterMetricKey,
  mode: TrendMode,
  baseVariantName: string,
): TrendChart {
  const series: TrendSeries[] = [];
  const seen = new Set<string>();
  if (mode === 'value') {
    series.push({ key: CONTROL_KEY, label: baseVariantName, isControl: true });
  }

  const rows = days.map((day) => {
    const row: TrendRow = { date: day.date };
    const points = day.points.filter((point) => point.metric === metric);
    if (mode === 'value' && points[0]) {
      row[CONTROL_KEY] = points[0].baseValue;
    }
    for (const point of points) {
      if (!seen.has(point.variantId)) {
        seen.add(point.variantId);
        series.push({ key: point.variantId, label: point.variantName, isControl: false });
      }
      row[point.variantId] = pointValue(point, mode);
    }
    return row;
  });

  return { rows, series };
}

/** Axis and tooltip formatting for a trend value, per mode and metric. */
export function formatTrendValue(mode: TrendMode, metric: MarketingRouterMetricKey, value: number): string {
  switch (mode) {
    case 'improvement': {
      const sign = value > 0 ? '+' : '';
      return `${sign}${value.toFixed(1)}%`;
    }
    case 'confidence':
      return `${value.toFixed(0)}%`;
    case 'value':
      return formatMetricValue(metric, value);
  }
}

export type VisitorsChart = { rows: TrendRow[]; series: TrendSeries[] };

/** New visitors per day, one bar per variant, control first. */
export function buildVisitorsChart(
  days: MarketingRouterSignificanceTimeseriesDay[],
  baseVariantId: string,
): VisitorsChart {
  const series: TrendSeries[] = [];
  const seen = new Set<string>();
  const rows = days.map((day) => {
    const row: TrendRow = { date: day.date };
    for (const entry of day.newVisitors) {
      if (!seen.has(entry.variantId)) {
        seen.add(entry.variantId);
        series.push({
          key: entry.variantId,
          label: entry.variantName,
          isControl: entry.variantId === baseVariantId,
        });
      }
      row[entry.variantId] = entry.visitors;
    }
    return row;
  });
  series.sort((left, right) => Number(right.isControl) - Number(left.isControl));
  return { rows, series };
}

/** Short day label for a dense x axis: "19 Aug". */
export function formatTrendDate(date: string): string {
  const [, month, day] = date.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIndex = Number(month) - 1;
  return monthNames[monthIndex] ? `${Number(day)} ${monthNames[monthIndex]}` : date;
}
